import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import useMasterMaps, { selCls } from "../hooks/useMasterMaps";
import ScopeToggle from "../components/ScopeToggle";
import { GRADES, VERTICALS } from "../lib/constants";
import { toast } from "sonner";
import { Download } from "lucide-react";

const fmtFill = (f) => (f == null ? "n/a" : `${(f * 100).toFixed(0)}%`);

function Tile({ label, value, color, testid, highlight }) {
  return (
    <div data-testid={testid} className={`bg-white border rounded-sm p-4 ${highlight ? "border-[#BB162B]" : "border-gray-200"}`}>
      <div className="text-2xl font-semibold tabular-nums" style={{ color }}>{value ?? "—"}</div>
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mt-1">{label}</div>
    </div>
  );
}

export default function SanctionMaster() {
  const { can } = useAuth();
  const maps = useMasterMaps();
  const [scope, setScope] = useState("Live");
  const [f, setF] = useState({ zone_id: "", branch_id: "", vertical: "", dept_id: "", grade: "" });
  const [data, setData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const params = useMemo(() => {
    const p = { branch_status: scope };
    Object.entries(f).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [scope, f]);

  useEffect(() => {
    setLoading(true);
    api.get("/sanctions/master", { params })
      .then(({ data }) => { setData(data); setPage(0); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [params]);

  const exportXlsx = async () => {
    try {
      const res = await api.get("/sanctions/master/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sanction_master.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const t = data.totals || {};
  const pageRows = data.rows.slice(page * pageSize, (page + 1) * pageSize);
  const branchOptions = f.zone_id ? maps.branches.filter((b) => b.zone_id === f.zone_id) : maps.branches;
  const deptOptions = f.vertical ? maps.departments.filter((d) => d.vertical === f.vertical) : maps.departments;

  const gapCell = (v, color) => (
    <td className="px-3 py-2.5 text-right tabular-nums font-medium" style={{ color: v > 0 ? color : "#9CA3AF" }}>{v}</td>
  );

  return (
    <div data-testid="sanction-master-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-[#0E2240] tracking-tight">Sanction Master</h1>
          <p className="text-sm text-[#6B7280] mt-1">Controlled master — counts change only via approved Sanction Change Requests</p>
        </div>
        <div className="flex items-center gap-3">
          <ScopeToggle value={scope} onChange={setScope} />
          {can("sanctions", "export") && (
            <button data-testid="sanction-export-button" onClick={exportXlsx}
              className="flex items-center gap-2 bg-[#0E2240] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#1a3358] transition-colors duration-200">
              <Download size={15} /> Excel
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-5">
        <Tile testid="tile-sanctioned" label="Sanctioned" value={t.sanctioned} color="#0E2240" />
        <Tile testid="tile-active" label="Active" value={t.active} color="#1B7F3B" />
        <Tile testid="tile-fill" label="Fill %" value={fmtFill(t.fill_percent)} color="#0E2240" />
        <Tile testid="tile-shortfall" label="Shortfall" value={t.shortfall} color="#BB162B" />
        <Tile testid="tile-surplus" label="Surplus" value={t.surplus} color="#1E5AA8" />
        <Tile testid="tile-projected" label="Projected Shortfall" value={t.projected_shortfall} color="#D97706" />
        <Tile testid="tile-covered" label="Covered" value={t.covered_shortfall} color="#1B7F3B" />
        <Tile testid="tile-uncovered" label="Uncovered" value={t.uncovered_shortfall} color="#BB162B" highlight />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <select data-testid="filter-zone" className={selCls} value={f.zone_id}
          onChange={(e) => setF((p) => ({ ...p, zone_id: e.target.value, branch_id: "" }))}>
          <option value="">All zones</option>
          {maps.zones.map((z) => <option key={z.zone_id} value={z.zone_id}>{z.zone_name}</option>)}
        </select>
        <select data-testid="filter-branch" className={selCls} value={f.branch_id}
          onChange={(e) => setF((p) => ({ ...p, branch_id: e.target.value }))}>
          <option value="">All branches</option>
          {branchOptions.map((b) => <option key={b.branch_id} value={b.branch_id}>{b.branch_name}</option>)}
        </select>
        <select data-testid="filter-vertical" className={selCls} value={f.vertical}
          onChange={(e) => setF((p) => ({ ...p, vertical: e.target.value, dept_id: "" }))}>
          <option value="">All verticals</option>
          {VERTICALS.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select data-testid="filter-dept" className={selCls} value={f.dept_id}
          onChange={(e) => setF((p) => ({ ...p, dept_id: e.target.value }))}>
          <option value="">All departments</option>
          {deptOptions.map((d) => <option key={d.dept_id} value={d.dept_id}>{d.dept_name}</option>)}
        </select>
        <select data-testid="filter-grade" className={selCls} value={f.grade}
          onChange={(e) => setF((p) => ({ ...p, grade: e.target.value }))}>
          <option value="">All grades</option>
          {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sanction-master-table">
            <thead>
              <tr className="border-b border-gray-200 bg-[#F5F6F8]">
                {["Branch", "Department", "Designation", "Grade"].map((h) => (
                  <th key={h} className="px-3 py-3 text-left text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold whitespace-nowrap">{h}</th>
                ))}
                {["Sanctioned", "Active", "On Notice", "Shortfall", "Surplus", "Fill %", "Projected", "Covered", "Uncovered"].map((h) => (
                  <th key={h} className="px-3 py-3 text-right text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={13} className="px-4 py-10 text-center text-[#6B7280]">Loading…</td></tr>
              ) : pageRows.length === 0 ? (
                <tr><td colSpan={13} className="px-4 py-10 text-center text-[#6B7280]">No positions match filters</td></tr>
              ) : pageRows.map((r, i) => (
                <tr key={`${r.branch_id}${r.dept_id}${r.desig_id}`} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors duration-200">
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    {r.branch_name}
                    {r.branch_status === "Greenfield" && <span className="ml-1.5 text-[9px] uppercase text-[#D97706] border border-[#D97706]/30 px-1 rounded-sm">GF</span>}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{r.dept_name}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{r.desig_name}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs text-[#6B7280]">{r.grade}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-medium">{r.sanctioned}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{r.active}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums" style={{ color: r.on_notice > 0 ? "#D97706" : "#9CA3AF" }}>{r.on_notice}</td>
                  {gapCell(r.shortfall, "#BB162B")}
                  {gapCell(r.surplus, "#1E5AA8")}
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtFill(r.fill_percent)}</td>
                  {gapCell(r.projected_shortfall, "#D97706")}
                  {gapCell(r.covered_shortfall, "#1B7F3B")}
                  {gapCell(r.uncovered_shortfall, "#BB162B")}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-gray-200 flex items-center justify-between text-xs text-[#6B7280]">
          <span className="tabular-nums">{data.rows.length} positions · page {page + 1} of {Math.max(1, Math.ceil(data.rows.length / pageSize))}</span>
          <div className="flex gap-2">
            <button data-testid="sanction-prev-page" disabled={page === 0} onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 border border-gray-300 rounded-sm disabled:opacity-40 hover:bg-gray-50 transition-colors duration-200">Prev</button>
            <button data-testid="sanction-next-page" disabled={(page + 1) * pageSize >= data.rows.length} onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 border border-gray-300 rounded-sm disabled:opacity-40 hover:bg-gray-50 transition-colors duration-200">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}
